/**
 * Shared utilities for deriving effective date ranges from asset assignments.
 * Used by monitoring, reporting, and usage components across sections.
 */

export interface AssignmentDateInput {
  id?: string | null
  start?: string | null
  end?: string | null
  active?: boolean | null
  status?: string | null
  monitored?: boolean | null
}

/**
 * Filter assignments to only those that are monitored, falling back to all
 * assignments if none are monitored. This ensures monitoring and reporting
 * components use date ranges that reflect actual data-collection windows.
 *
 * @param allAssignments - The full list of assignments for the asset.
 * @param monitoredIds - Array of assignment IDs flagged as monitored (from assignments.monitored).
 * @returns The monitored subset, or the full list if no monitored assignments exist.
 */
export function getMonitoredAssignments(
  allAssignments: AssignmentDateInput[],
  monitoredIds?: (string | null)[] | null
): AssignmentDateInput[] {
  if (!allAssignments?.length) return []

  // If we have explicit monitored IDs, filter to those
  if (monitoredIds?.length) {
    const idSet = new Set(monitoredIds.filter(Boolean))
    const monitored = allAssignments.filter(a => a.id && idSet.has(a.id))
    if (monitored.length > 0) return monitored
  }

  // Fallback: check the `monitored` boolean flag on each assignment
  const monitoredByFlag = allAssignments.filter(a => a.monitored)
  if (monitoredByFlag.length > 0) return monitoredByFlag

  // Final fallback: use all assignments
  return allAssignments
}

/**
 * Ensure a timestamp string is treated as UTC by appending 'Z' if it has no
 * timezone indicator. Backend stores timestamps as UTC but sometimes omits the
 * suffix, causing `new Date()` to misinterpret them as local time.
 */
function ensureUtc(timestamp: string): string {
  // Already has timezone info (Z, +HH:MM, -HH:MM)
  if (/Z|[+-]\d{2}:\d{2}$/.test(timestamp)) return timestamp
  return timestamp + "Z"
}

/**
 * Filter assignments to those relevant for date range computation.
 * An assignment is relevant if it has ended (has an `end` date) or is currently active.
 */
function getRelevantAssignments(assignments: AssignmentDateInput[]): AssignmentDateInput[] {
  return assignments.filter(a => a.end || a.active || a.status === "active")
}

/**
 * Compute the latest end date from an asset's assignments.
 *
 * If any assignment is currently active (no end date), returns undefined —
 * the caller should fall back to "now" since the asset is still live.
 * Otherwise finds the most recent end date among completed assignments.
 */
export function getAssignmentEndDate(assignments: AssignmentDateInput[]): string | undefined {
  const relevant = getRelevantAssignments(assignments)
  if (relevant.length === 0) return undefined

  // If any assignment is currently active (no end), the asset is still live → use "now"
  if (relevant.some(a => (a.active || a.status === "active") && !a.end)) {
    return undefined
  }

  // All assignments have ended — find the latest end date
  const sorted = [...relevant].sort((a, b) => {
    const endA = a.end ? new Date(a.end).getTime() : 0
    const endB = b.end ? new Date(b.end).getTime() : 0
    return endB - endA
  })

  return sorted[0].end ? new Date(ensureUtc(sorted[0].end)).toISOString() : undefined
}

/**
 * Compute the earliest start date from an asset's assignments.
 *
 * Finds the earliest start date among relevant assignments. Returns undefined
 * if no relevant assignment has a start date — the caller should use a
 * sensible fallback (e.g. 7 or 30 days ago).
 */
export function getAssignmentStartDate(assignments: AssignmentDateInput[]): string | undefined {
  const relevant = getRelevantAssignments(assignments)
  if (relevant.length === 0) return undefined

  // Sort by earliest start date
  const sorted = [...relevant].sort((a, b) => {
    const startA = a.start ? new Date(a.start).getTime() : Infinity
    const startB = b.start ? new Date(b.start).getTime() : Infinity
    return startA - startB
  })

  return sorted[0].start ? new Date(ensureUtc(sorted[0].start)).toISOString() : undefined
}
